// 변경공사 금액 — 항목은 양수로 두고 방향으로 부호를 붙인다(RPC 는 계산하지 않고 이 결과를 쓴다).

import type { Contract, ContractPayment } from "../contract/rows";
import { checked } from "../money/checked";
import { amountsFromSupply, amountsFromTotal, VatMode } from "../money/vat";
import { calcItem } from "../quote/calc";
import { BelowPaidError } from "./errors";
import type { ChangeOrder, ChangeOrderDirection } from "./types";

export interface ChangeOrderItemInput {
  direction: ChangeOrderDirection;
  qty: number;
  material_unit_price: number;
  labor_unit_price: number;
}

export interface ChangeOrderLine {
  direction: ChangeOrderDirection;
  material_amount: number;
  labor_amount: number;
  amount: number;
  // 방향을 붙인 금액 — 감액이면 음수.
  signed_amount: number;
}

export interface ChangeOrderCalc {
  lines: ChangeOrderLine[];
  add_total: number;
  deduct_total: number;
  delta_total: number;
  delta_supply: number;
  delta_vat: number;
}

const total = (values: number[]): number =>
  checked(
    values.reduce((a, b) => a + b, 0),
    "합계"
  );

// 계약의 부가세 표기가 곧 변경공사 단가의 기준이다 — 포함 계약이면 INCL, 별도 계약이면 EXCL.
export const contractVatModeOf = (contract: Partial<Pick<Contract, "vat_included">>): VatMode =>
  contract.vat_included === false ? "EXCL" : "INCL";

export const calcChangeOrder = (
  items: readonly ChangeOrderItemInput[],
  vatMode: VatMode = "INCL"
): ChangeOrderCalc => {
  const lines = items.map((it): ChangeOrderLine => {
    const r = calcItem({
      qty: it.qty,
      material_unit_price: it.material_unit_price,
      labor_unit_price: it.labor_unit_price,
    });
    return {
      direction: it.direction,
      material_amount: r.material_amount,
      labor_amount: r.labor_amount,
      amount: r.amount,
      signed_amount: it.direction === "DEDUCT" ? -r.amount : r.amount,
    };
  });
  const add_total = total(lines.filter((l) => l.direction === "ADD").map((l) => l.amount));
  const deduct_total = total(lines.filter((l) => l.direction === "DEDUCT").map((l) => l.amount));
  const net = checked(add_total - deduct_total, "증감액");
  // 절댓값으로 나누고 부호를 붙인다 — 별도면 줄 합이 공급가라 부가세를 얹고, delta_total 은 언제나 부가세 포함이다.
  const split = vatMode === "EXCL" ? amountsFromSupply(Math.abs(net)) : amountsFromTotal(Math.abs(net));
  const sign = net < 0 ? -1 : 1;
  const delta_total = checked(sign * split.total_amount, "증감액");
  return {
    lines,
    add_total,
    deduct_total,
    delta_total,
    delta_supply: sign * split.supply_amount,
    delta_vat: sign * split.vat_amount,
  };
};

export interface CurrentTotal {
  original: number;
  accepted_delta: number;
  current: number;
  current_supply: number;
  current_vat: number;
}

// 현재 계약금액 = 원 계약금액 + 승인된 증감(★3 이 SQL 에서 같은 식으로 대조) · 포함 계약은 현재 총액을 다시 나누고(±1원 방지), 별도 계약은 공급가·세액 조각을 더한다(공급가 기준이라 역산하면 어긋난다).
export const currentTotalOf = (
  contract: Pick<Contract, "total_amount" | "supply_amount" | "vat_amount"> & Partial<Pick<Contract, "vat_included">>,
  changeOrders: readonly Pick<ChangeOrder, "status" | "delta_total" | "delta_supply" | "delta_vat">[]
): CurrentTotal => {
  const accepted = changeOrders.filter((co) => co.status === "ACCEPTED");
  const accepted_delta = total(accepted.map((co) => co.delta_total));
  const current = checked(contract.total_amount + accepted_delta, "현재 계약금액");
  const summed = () => ({
    supply_amount: checked(contract.supply_amount + total(accepted.map((co) => co.delta_supply)), "현재 공급가액"),
    vat_amount: checked(contract.vat_amount + total(accepted.map((co) => co.delta_vat)), "현재 부가세액"),
  });
  // 음수(감액이 원 계약을 넘는 잘못된 상태)는 나누지 못하므로 조각을 더한다 — 부르는 쪽이 음수를 걸러 낸다.
  const splitOf = (): { supply_amount: number; vat_amount: number } => {
    if (accepted.length === 0) return { supply_amount: contract.supply_amount, vat_amount: contract.vat_amount };
    if (current < 0 || contractVatModeOf(contract) === "EXCL") return summed();
    return amountsFromTotal(current);
  };
  const split = splitOf();
  return {
    original: contract.total_amount,
    accepted_delta,
    current,
    current_supply: split.supply_amount,
    current_vat: split.vat_amount,
  };
};

export const paidSumOf = (payments: readonly Pick<ContractPayment, "amount" | "paid_at">[]): number =>
  total(payments.filter((p) => p.paid_at !== null).map((p) => p.amount));

// 변경 후 금액이 0 이상 · 기지급 합 이상 · 안전 정수인지 본다(변경 후 금액을 돌려준다).
export const assertDeltaAllowed = (current: number, delta: number, paidSum: number): number => {
  const next = checked(checked(current, "현재 계약금액") + checked(delta, "증감액"), "변경 후 금액");
  if (next < 0) {
    throw new Error("변경 후 계약금액은 0원 이상이어야 합니다");
  }
  if (next < paidSum) {
    throw new BelowPaidError(paidSum, next);
  }
  return next;
};
